import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ChevronDown, LogOut, LogIn } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import SocialLinks from "@/components/SocialLinks";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { t, lang, setLang, langs } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/stock", label: t.nav.stock },
    { to: "/sur-nos-routes", label: t.nav.on_the_road },
    { to: "/services", label: t.nav.services },
    { to: "/showroom", label: t.nav.showroom },
    { to: "/simulator", label: t.nav.simulator },
    { to: "/blog", label: "Blog" },
    { to: "/about", label: t.nav.about },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 border-b ${
        scrolled
          ? "bg-[#0D0D0D]/85 backdrop-blur-xl border-white/10"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <Logo asLink={false} size="w-11 h-11" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm tracking-wide uppercase transition-colors duration-300 ${
                  isActive ? "text-[#C8A75A]" : "text-white/70 hover:text-white"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {/* CTA prestige : Vendre ma voiture — visible en permanence */}
          <Link
            to="/sell"
            data-testid="nav-sell-cta"
            className="hidden md:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-black bg-[#C8A75A] hover:bg-white transition-colors duration-300 rounded-md px-4 py-2.5 shadow-[0_6px_20px_-4px_rgba(200,167,90,0.5)]"
          >
            Vendre ma voiture
          </Link>

          <SocialLinks variant="nav" className="hidden lg:flex" />

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="lang-switcher"
              className="text-xs uppercase tracking-[0.2em] text-white/70 hover:text-[#C8A75A] flex items-center gap-1 border border-white/10 rounded-md px-3 py-2"
            >
              {lang.toUpperCase()} <ChevronDown className="w-3 h-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#1E1E1E] border-white/10 text-white min-w-[80px]">
              {langs.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`text-xs tracking-wide cursor-pointer ${
                    lang === l.code ? "text-[#C8A75A]" : ""
                  }`}
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user?.role === "admin" ? (
            <div className="hidden lg:flex items-center gap-2">
              <button
                data-testid="nav-admin-link"
                onClick={() => nav("/admin")}
                className="text-xs uppercase tracking-[0.2em] text-[#C8A75A] hover:text-white transition-colors border border-[#C8A75A]/40 rounded-md px-3 py-2"
              >
                {t.nav.admin}
              </button>
              <button
                data-testid="nav-logout"
                onClick={logout}
                className="text-white/60 hover:text-white p-2"
                aria-label="logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : null}

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white p-2"
            aria-label="menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#0D0D0D]/98 backdrop-blur-xl border-t border-white/10"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-white/80 hover:text-[#C8A75A] text-sm uppercase tracking-[0.2em]"
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/sell"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-sell-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-black bg-[#C8A75A] hover:bg-white transition-colors rounded-md px-4 py-3"
            >
              Vendre ma voiture
            </Link>
            {user?.role === "admin" ? (
              <NavLink
                to="/admin"
                onClick={() => setOpen(false)}
                className="text-[#C8A75A] text-sm uppercase tracking-[0.2em]"
              >
                {t.nav.admin}
              </NavLink>
            ) : null}
            <div className="pt-4 mt-2 border-t border-white/10">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Nous suivre</p>
              <SocialLinks variant="footer" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
